<template>
  <div class="sign-integration-container">
    <!-- Login 部分 -->
    <div v-if="!isLoggedIn" class="login-section">
      <div class="card">
        <h2>簽署整合管理系統</h2>
        <p class="subtitle">請輸入 API Token 以繼續</p>

        <div class="form-group">
          <label for="token">API Token</label>
          <input
            id="token"
            v-model="tempToken"
            type="password"
            placeholder="輸入您的 API Token"
            class="input-field"
            @keyup.enter="login"
          />
        </div>

        <div class="test-credentials">
          <p><strong>測試帳戶</strong></p>
          <p>測試 Token 已隱藏，請向系統管理員索取。</p>
        </div>

        <button @click="login" class="btn btn-primary" :disabled="!tempToken">
          登入
        </button>
      </div>
    </div>

    <!-- 主要應用部分 -->
    <div v-else class="app-section">
      <!-- Header -->
      <header class="header">
        <h1>簽署整合管理系統</h1>
        <button @click="logout" class="btn btn-outline">登出</button>
      </header>

      <!-- 標籤頁 -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Tab 1: 建立簽署任務 -->
      <div v-if="activeTab === 'create'" class="tab-content">
        <div class="card">
          <h2>建立簽署任務</h2>

          <form @submit.prevent="submitCreateTask">
            <div class="form-group">
              <label for="contractNo">合約編號 *</label>
              <input
                id="contractNo"
                v-model="formData.contract_no"
                type="text"
                placeholder="例如: CONTRACT-2026-001"
                class="input-field"
                required
              />
            </div>

            <div class="form-group">
              <label for="signerEmail">簽署人郵箱 *</label>
              <input
                id="signerEmail"
                v-model="formData.signer_email"
                type="email"
                placeholder="例如: signer@example.com"
                class="input-field"
                required
              />
            </div>

            <div class="form-group">
              <label for="fileName">檔案名稱</label>
              <input
                id="fileName"
                v-model="formData.file_name"
                type="text"
                placeholder="例如: contract.pdf (若不填自動生成)"
                class="input-field"
              />
            </div>

            <div class="form-group">
              <label for="fileUpload">上傳 PDF 檔案 *</label>
              <div class="file-upload">
                <input
                  id="fileUpload"
                  type="file"
                  accept=".pdf"
                  class="file-input"
                  @change="handleFileUpload"
                />
                <span v-if="fileSelected" class="file-selected">
                  ✓ {{ fileSelected }}
                </span>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                {{ isSubmitting ? '提交中...' : '建立簽署任務' }}
              </button>
            </div>

            <!-- 提交結果 -->
            <div v-if="submitResult" :class="['alert', submitResult.type]">
              <strong>{{ submitResult.type === 'success' ? '✓ 成功' : '✗ 失敗' }}</strong>
              <p>{{ submitResult.message }}</p>
              <div v-if="submitResult.data" class="result-data">
                <pre>{{ JSON.stringify(submitResult.data, null, 2) }}</pre>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Tab 2: 查詢狀態 -->
      <div v-if="activeTab === 'status'" class="tab-content">
        <div class="card">
          <h2>查詢簽署狀態</h2>

          <div class="form-group">
            <label for="taskId">Task ID</label>
            <input
              id="taskId"
              v-model="queryTaskId"
              type="text"
              placeholder="例如: TSK-12345"
              class="input-field"
            />
          </div>

          <button
            @click="queryStatus"
            class="btn btn-primary"
            :disabled="!queryTaskId || isQuerying"
          >
            {{ isQuerying ? '查詢中...' : '查詢狀態' }}
          </button>

          <!-- 查詢結果 -->
          <div v-if="queryResult" class="result-section">
            <div v-if="queryResult.error" class="alert error">
              <strong>✗ 失敗</strong>
              <p>{{ queryResult.error }}</p>
            </div>

            <div v-else>
              <div class="status-grid">
                <div class="status-item">
                  <span class="label">狀態</span>
                  <span :class="['value', `status-${queryResult.sign_status}`]">
                    {{ translateStatus(queryResult.sign_status) }}
                  </span>
                </div>
                <div class="status-item">
                  <span class="label">合約編號</span>
                  <span class="value">{{ queryResult.contract_no }}</span>
                </div>
                <div class="status-item">
                  <span class="label">Task ID</span>
                  <span class="value">{{ queryResult.task_id }}</span>
                </div>
                <div class="status-item">
                  <span class="label">補跑次數</span>
                  <span class="value">{{ queryResult.retry_count }}</span>
                </div>
                <div v-if="queryResult.last_error" class="status-item full-width">
                  <span class="label">最後錯誤</span>
                  <span class="value error">{{ queryResult.last_error }}</span>
                </div>
                <div class="status-item full-width">
                  <span class="label">最後處理時間</span>
                  <span class="value">{{ formatDate(queryResult.processed_at) }}</span>
                </div>
              </div>

              <!-- 補跑按鈕 -->
              <div
                v-if="['failed', 'partial_failed'].includes(queryResult.sign_status)"
                class="action-buttons"
              >
                <button
                  @click="resendTask(queryResult.task_id)"
                  class="btn btn-warning"
                  :disabled="isResending"
                >
                  {{ isResending ? '處理中...' : '標記補跑' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab 3: 說明文檔 -->
      <div v-if="activeTab === 'docs'" class="tab-content">
        <div class="card">
          <h2>API 說明文檔</h2>

          <div class="docs-section">
            <h3>1. 建立簽署任務</h3>
            <p>用於建立新的簽署任務，觸發 DotSign 簽署流程</p>
            <ul>
              <li><strong>需求權限</strong>: sign-integration.create</li>
              <li><strong>返回值</strong>: task_id（用於後續查詢和補跑）</li>
              <li><strong>備註</strong>: 檔案需為 PDF 格式</li>
            </ul>

            <h3>2. 查詢簽署狀態</h3>
            <p>根據 Task ID 查詢簽署進度和結果</p>
            <ul>
              <li><strong>需求權限</strong>: sign-integration.view</li>
              <li><strong>可能狀態</strong>:
                <ul>
                  <li>pending - 等待中</li>
                  <li>signing - 簽署中</li>
                  <li>completed - 已完成</li>
                  <li>failed - 失敗</li>
                  <li>partial_failed - 部分失敗</li>
                </ul>
              </li>
            </ul>

            <h3>3. 補跑失敗任務</h3>
            <p>對於失敗的任務，可標記進行重新處理</p>
            <ul>
              <li><strong>需求權限</strong>: sign-integration.resend</li>
              <li><strong>適用狀態</strong>: failed 或 partial_failed</li>
              <li><strong>執行方式</strong>: 由後台排程每 10 分鐘自動執行</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import signIntegrationApi from '../services/signIntegrationApi'

const STATUS_LABELS = {
  pending: '等待中',
  signing: '簽署中',
  completed: '已完成',
  failed: '失敗',
  partial_failed: '部分失敗',
}

export default {
  name: 'SignIntegrationApp',
  data() {
    return {
      isLoggedIn: false,
      tempToken: '',
      activeTab: 'create',
      tabs: [
        { id: 'create', name: '建立簽署任務' },
        { id: 'status', name: '查詢狀態' },
        { id: 'docs', name: '說明文檔' },
      ],

      // 表單資料
      formData: {
        contract_no: '',
        signer_email: '',
        file_name: '',
        file_base64: '',
      },
      fileSelected: null,
      isSubmitting: false,
      submitResult: null,

      // 查詢資料
      queryTaskId: '',
      isQuerying: false,
      queryResult: null,

      // 補跑
      isResending: false,
    }
  },
  mounted() {
    // 檢查是否已登入
    const token = localStorage.getItem('api_token')
    if (token) {
      this.isLoggedIn = true
    }
  },
  methods: {
    login() {
      if (!this.tempToken.trim()) {
        alert('請輸入 API Token')
        return
      }
      signIntegrationApi.setToken(this.tempToken)
      this.isLoggedIn = true
      this.tempToken = ''
    },

    logout() {
      signIntegrationApi.clearToken()
      this.isLoggedIn = false
      this.resetForm()
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      this.fileSelected = file.name

      const reader = new FileReader()
      reader.onload = (e) => {
        const binary = e.target.result
        this.formData.file_base64 = btoa(binary)
        if (!this.formData.file_name) {
          this.formData.file_name = file.name
        }
      }
      reader.readAsBinaryString(file)
    },

    async submitCreateTask() {
      if (!this.formData.contract_no || !this.formData.signer_email || !this.formData.file_base64) {
        alert('請填寫所有必填欄位')
        return
      }

      this.isSubmitting = true
      this.submitResult = null

      try {
        const response = await signIntegrationApi.createSignTask({
          contract_no: this.formData.contract_no,
          signer_email: this.formData.signer_email,
          file_base64: this.formData.file_base64,
          file_name: this.formData.file_name || 'contract.pdf',
        })

        this.submitResult = {
          type: 'success',
          message: response.message || '簽署任務建立成功！',
          data: response.data,
        }

        // 成功後清空表單
        this.resetForm()
      } catch (error) {
        this.submitResult = {
          type: 'error',
          message: error.message || '建立失敗',
          data: error.data,
        }
      } finally {
        this.isSubmitting = false
      }
    },

    async queryStatus() {
      if (!this.queryTaskId.trim()) {
        alert('請輸入 Task ID')
        return
      }

      this.isQuerying = true
      this.queryResult = null

      try {
        const response = await signIntegrationApi.getSignStatus(this.queryTaskId)
        this.queryResult = response.data
      } catch (error) {
        this.queryResult = {
          error: error.message || '查詢失敗',
        }
      } finally {
        this.isQuerying = false
      }
    },

    async resendTask(taskId) {
      if (!confirm('確認要標記此任務進行補跑嗎？')) {
        return
      }

      this.isResending = true

      try {
        const response = await signIntegrationApi.resendFailedTask(taskId)
        alert(`成功標記補跑！補跑次數: ${response.data.retry_count}`)
        // 重新查詢狀態
        this.queryTaskId = taskId
        await this.queryStatus()
      } catch (error) {
        alert(`補跑失敗: ${error.message}`)
      } finally {
        this.isResending = false
      }
    },

    translateStatus(status) {
      return STATUS_LABELS[status] || status
    },

    formatDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleString('zh-TW')
    },

    resetForm() {
      this.formData = {
        contract_no: '',
        signer_email: '',
        file_name: '',
        file_base64: '',
      }
      this.fileSelected = null
    },
  },
}
</script>

<style scoped>
:root {
  --primary-color: #0066cc;
  --primary-dark: #0052a3;
  --secondary-color: #f0f2f5;
  --text-color: #262626;
  --text-light: #8a8d91;
  --border-color: #d9d9d9;
  --success-color: #52c41a;
  --error-color: #ff4d4f;
  --warning-color: #faad14;
  --completed-color: #52c41a;
  --failed-color: #ff4d4f;
  --partial-failed-color: #faad14;
}

* {
  box-sizing: border-box;
}

.sign-integration-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
    Arial, sans-serif;
  padding: 20px;
}

/* Login Section */
.login-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
}

.card h2 {
  margin: 0 0 10px;
  color: var(--text-color);
  font-size: 28px;
  font-weight: 600;
}

.subtitle {
  color: var(--text-light);
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-color);
  font-weight: 500;
}

.input-field {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.input-field:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
}

.test-credentials {
  background: var(--secondary-color);
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 13px;
}

.test-credentials p {
  margin: 0 0 10px;
  font-weight: 600;
  color: var(--text-color);
}

.test-credentials ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.test-credentials li {
  margin-bottom: 5px;
  color: var(--text-light);
}

.test-credentials code {
  background: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
}

/* Buttons */
.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  width: 100%;
  margin-bottom: 10px;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-dark);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline {
  background: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  padding: 8px 16px;
}

.btn-outline:hover {
  background: var(--primary-color);
  color: white;
}

.btn-warning {
  background: var(--warning-color);
  color: white;
}

.btn-warning:hover:not(:disabled) {
  opacity: 0.8;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* App Section */
.app-section {
  max-width: 1000px;
  margin: 0 auto;
}

.header {
  background: white;
  padding: 20px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.header h1 {
  margin: 0;
  color: var(--text-color);
  font-size: 24px;
}

.tabs {
  background: white;
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tab {
  padding: 15px 24px;
  border: none;
  background: transparent;
  color: var(--text-light);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
}

.tab:hover {
  color: var(--text-color);
}

.tab.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.tab-content {
  background: white;
  padding: 30px;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.tab-content .card {
  background: transparent;
  padding: 0;
  box-shadow: none;
  max-width: 100%;
}

.file-upload {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--secondary-color);
  border: 2px dashed var(--border-color);
  border-radius: 6px;
  cursor: pointer;
}

.file-input {
  flex: 1;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.file-selected {
  color: var(--success-color);
  font-weight: 500;
}

.form-actions {
  margin-top: 20px;
}

.alert {
  padding: 16px;
  border-radius: 6px;
  margin-top: 20px;
}

.alert.success {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #274000;
}

.alert.error {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  color: #820000;
}

.alert strong {
  display: block;
  margin-bottom: 8px;
}

.result-data {
  background: var(--secondary-color);
  padding: 12px;
  border-radius: 4px;
  margin-top: 12px;
  overflow-x: auto;
}

.result-data pre {
  margin: 0;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

.result-section {
  margin-top: 20px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.status-item {
  background: var(--secondary-color);
  padding: 16px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-item.full-width {
  grid-column: 1 / -1;
}

.status-item .label {
  font-size: 12px;
  color: var(--text-light);
  font-weight: 500;
  text-transform: uppercase;
}

.status-item .value {
  font-size: 16px;
  color: var(--text-color);
  font-weight: 600;
}

.status-item .value.error {
  color: var(--error-color);
}

.status-pending {
  color: var(--text-light);
}

.status-signing {
  color: var(--primary-color);
}

.status-completed {
  color: var(--completed-color);
}

.status-failed {
  color: var(--failed-color);
}

.status-partial_failed {
  color: var(--partial-failed-color);
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.action-buttons button {
  flex: 1;
}

.docs-section {
  color: var(--text-color);
}

.docs-section h3 {
  margin-top: 20px;
  margin-bottom: 10px;
  color: var(--text-color);
  font-size: 16px;
}

.docs-section h3:first-child {
  margin-top: 0;
}

.docs-section p {
  margin: 0 0 10px;
  color: var(--text-light);
}

.docs-section ul {
  margin: 10px 0;
  padding-left: 20px;
}

.docs-section li {
  margin-bottom: 6px;
  color: var(--text-light);
}

@media (max-width: 768px) {
  .card {
    padding: 24px;
  }

  .header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .tabs {
    overflow-x: auto;
  }

  .tab {
    white-space: nowrap;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }
}
</style>
